import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useGameDispatch } from '../../store/GameStore';
import { highflyerStateActions } from '../../store/slices/highflyerState/slice';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';

import { IconButtonWithTooltip, Icons, IconButtonSize } from '@pp/core/src/components/controls/iconButton/IconButtonWithTooltip';
import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { useCashierCallbackConnected } from '@pp/core/src/hooks/useCashierCallbackConnected';
import { useNetPositionConnected } from '@pp/core/src/hooks/useNetPositionConnected';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useSessionBalanceConfig } from '@pp/core/src/store/compositeSelectors/useSessionBalanceConfig';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { TotalBetValueConnected } from '@pp/core/src/components/game/wallet/values/TotalBetValueConnected';
import { useLastWinDisplayConnected } from '@pp/core/src/components/game/wallet/lastWin/useLastWinDisplayConnected';
import { LastWinValueConnected } from '@pp/core/src/components/game/wallet/values/LastWinValueConnected';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import styles from './DesktopFooterConnected.module.css';

export const DesktopBalanceFooter: FC = () => {
  const net = useNetPositionConnected();
  const jurisdiction = useConfigSlice.playerConfig()?.jurisdiction;
  const balance = useBetSlice.getCompositeBalance();
  const hideBonusBalance = useConfigSlice.getShouldMergeBonusBalance();
  const displayBonusBalance = !hideBonusBalance || jurisdiction === 'X1';
  const cashierCallback = useCashierCallbackConnected();
  const compliance = useConfigSlice.compliance();
  const sessionBalance = useSessionBalanceConfig();
  const sessionBalanceWasSet = compliance?.sessionBalanceWasSet;
  const dispatch = useGameDispatch();

  const tableBetLimits = useConfigSlice.tableBetLimits();
  const balanceContainerRef = useRef<any>(null); // Using this ref to get the width of the container with balance, and assign min width of that block, to avoid jerk issue when balance changes.
  const [balanceContainerWidth, setBalanceContainerWidth] = useState(0);

  const totalBetHiddenRef = useRef<any>(null);
  const [totalBetWidth, setTotalBetWidth] = useState(0);

  const sizeChangedRef = useRef(false);
  const hasAnyEarlyBets = useHighflyerStateSlice.hasAnyEarlyBets();
  const lastWin = hasAnyEarlyBets ? 0 : useLastWinDisplayConnected();

  const lastWin_mods = useModifiers(styles, 'footerWrapper_balanceContainer_totalBet', {
    lastWin: lastWin ? true : false,
  },{ withBaseClass: true });

  useLayoutEffect(() => {
    if (balance?.amount === undefined) return;
    dispatch(highflyerStateActions.updateBalance(balance.amount + balance.bonus));
  }, [balance]);

  if (balance?.amount === undefined || !tableBetLimits) return null;

  const balAmount =
    sessionBalance && !sessionBalanceWasSet && balance.amount > 0 ? '-' : <CurrencyConnected value={balance.amount} />;

  useEffect(() => {
    if (!sizeChangedRef.current && balAmount !== '-') {
      const balanceContainerOffsetWidth = balanceContainerRef?.current?.offsetWidth + 8;
      balanceContainerRef.current && setBalanceContainerWidth(balanceContainerOffsetWidth);

      // Here we are setting min-width for total bet component.
      // we are adding an offset to accomodate for character size shifts.

      const calcWidth = totalBetHiddenRef?.current?.offsetWidth + 8;

      totalBetHiddenRef.current && setTotalBetWidth(calcWidth);
      sizeChangedRef.current = true;
    }
  }, [balAmount]);

  return (
    <div className={styles.footerWrapper}>
      <div className={styles.footerWrapper_juridictionInfo}>
        {/* Net Cash */}
        {net !== undefined && (
          <div className={styles.footerWrapper_juridictionInfo_netPosition}>
            <div className={styles.footerWrapper_juridictionInfo_label}>
              <L10nText tk="NET_POSITION" />
            </div>
            <div className={styles.footerWrapper_juridictionInfo_amt}>
              <CurrencyConnected value={net} />
            </div>
          </div>
        )}
        {/* Net Cash Ends */}

        {/* Bonus */}
        {displayBonusBalance && balance && (
          <div className={styles.footerWrapper_juridictionInfo_bonusBal}>
            <div className={styles.footerWrapper_juridictionInfo_label}>
              <L10nText tk="BONUS" />
            </div>
            <div className={styles.footerWrapper_juridictionInfo_amt}>
              <CurrencyConnected value={balance?.bonus} />
            </div>
          </div>
        )}
        {/* Bonus Ends */}
      </div>
      <div className={styles.footerWrapper_balanceContainer}>
        {/* Cashier Icon */}
        {cashierCallback && (
          <>
            <div className={styles.footerWrapper_balanceContainer_cashier}>
              <IconButtonWithTooltip
                size={IconButtonSize.SMALL}
                icon={Icons.Cashier}
                onClick={cashierCallback}
                tooltipOptions={{ text: 'CASHIER' }}
              />
            </div>
            <div className={styles.footerWrapper_balanceContainer_divider}></div>
          </>
        )}
        {/* Cashier Icon Ends*/}

        {/* Balance */}
        <div className={styles.footerWrapper_balanceContainer_balance} ref={balanceContainerRef}>
          <div className={styles.footerWrapper_balanceContainer_label}>
            <L10nText tk="BALANCE" />
          </div>
          <div className={styles.footerWrapper_balanceContainer_amt} style={{ minWidth: `${balanceContainerWidth}px` }}>
            {balAmount}
          </div>
        </div>
        <div className={styles.footerWrapper_balanceContainer_divider}></div>
        {/* Balance Ends*/}

        {/* Total Bet */}
        <div className={lastWin_mods} style={{ minWidth: `${totalBetWidth}px` }}>
          <div className={styles.footerWrapper_balanceContainer_label}>
          {lastWin ? <L10nText tk="LASTWIN" /> : <L10nText tk="BET" />}
          </div>
          <div className={styles.footerWrapper_balanceContainer_amt}>
            {lastWin ? <LastWinValueConnected value={lastWin} /> : <TotalBetValueConnected />}
          </div>
        </div>
        {/* Total Bet Ends*/}

        {/* We're creating hidden total max bet div to get the minimum width of that div, so that we can fix min width of the total Bet block to avoid jerk issues*/}
        <div className={styles.footerWrapper_balanceContainer_hiddenTotalBetAmount} ref={totalBetHiddenRef}>
          <CurrencyConnected value={tableBetLimits.max} />
        </div>
      </div>
    </div>
  );
};
